import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBirthdayCake } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import { typeImage } from '../../components/Utils/typeImage';
import AvatarCircle from '../../components/Utils/AvatarCircle';
import ModifierMonProfil from './ModifierMonProfil';
import PastillesCompetences from './PastillesCompetences';

/**
 * Profil enrichi du salarié connecté, en lecture : photo, nom, jalon
 * d'ancienneté, bio sur deux lignes, compétences et langues en pastilles
 * (YRH-NEW-008, Yros/Kavero#479). Tout se modifie dans la modale « Modifier mon
 * profil », en un seul enregistrement.
 *
 * Consomme GET /api/rh/mon-profil via le proxy. Self-service : la fiche est
 * celle de la session, aucun identifiant d'employé n'est jamais envoyé.
 */

// Au-delà, deux lignes ne suffisent sans doute pas : on propose « Voir plus ».
// jsdom ne mesure rien, et le style `line-clamp-2` limite l'affichage.
const BIO_COURTE = 140;

const bioEstLongue = (bio) => bio.length > BIO_COURTE || bio.split('\n').length > 2;

function MonProfilEnrichi({ nom }) {
  const { t } = useTranslation();
  const [profil, setProfil] = useState(null);
  const [ouverte, setOuverte] = useState(false);
  const [bioDepliee, setBioDepliee] = useState(false);
  const [catalogue, setCatalogue] = useState(null);

  const charger = useCallback(async () => {
    try {
      const donnees = await apiService.lireMonProfil();
      if (donnees && donnees.ok) setProfil(donnees);
    } catch (e) {
      // Un compte sans fiche ou une panne : l'écran « Mes informations »
      // qui l'accueille garde son contenu, sans cette carte.
      signalerErreur(e, 'MonProfilEnrichi.charger');
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  // Sans catalogue (panne, ou route absente en amont), la modale montre les
  // compétences et les langues en pastilles, sans pouvoir les modifier.
  useEffect(() => {
    (async () => {
      try {
        const reponse = await apiService.lireCatalogueCompetences();
        if (reponse && reponse.ok) setCatalogue(reponse);
      } catch (e) {
        signalerErreur(e, 'MonProfilEnrichi.catalogue');
      }
    })();
  }, []);

  if (!profil) return null;

  const annees = profil.jalon_anciennete?.annees;
  const bio = profil.bio || '';
  const longue = bioEstLongue(bio);

  return (
    <>
      <div
        className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100 space-y-3"
        data-testid="mon-profil-enrichi"
      >
        {/* Sur mobile (375 px), le bouton passe sous le nom au lieu de le chevaucher. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          {profil.photo ? (
            <img
              data-testid="profil-photo"
              src={`data:${typeImage(profil.photo)};base64,${profil.photo}`}
              alt={t('monProfil.photo')}
              className="w-16 h-16 rounded-full object-cover border border-slate-200 flex-shrink-0"
            />
          ) : (
            <span data-testid="profil-avatar" className="flex-shrink-0">
              <AvatarCircle name={nom} size={64} />
            </span>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-[10rem] flex-1">
            <span data-testid="profil-nom" className="text-base font-bold text-slate-800 break-words">
              {nom}
            </span>
            {annees ? (
              <span
                data-testid="jalon-anciennete"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold"
              >
                <FaBirthdayCake className="w-3 h-3 flex-shrink-0" />
                {t('monProfil.jalon', { count: annees })}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            data-testid="profil-modifier"
            onClick={() => setOuverte(true)}
            className="w-full sm:w-auto px-4 py-2 text-sm bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] transition-colors duration-200 flex-shrink-0"
          >
            {t('monProfil.modifier')}
          </button>
        </div>

        <div>
          <p
            data-testid="profil-bio-texte"
            className={`text-sm whitespace-pre-line break-words ${
              bio ? 'text-slate-700' : 'text-slate-400'
            } ${longue && !bioDepliee ? 'line-clamp-2' : ''}`}
          >
            {bio || t('monProfil.bioVide')}
          </p>
          {longue && (
            <button
              type="button"
              data-testid="profil-bio-plus"
              onClick={() => setBioDepliee((v) => !v)}
              className="mt-0.5 text-xs text-[--color-primary] hover:underline"
            >
              {bioDepliee ? t('monProfil.voirMoins') : t('monProfil.voirPlus')}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              {t('monProfil.competences')}
            </p>
            <PastillesCompetences lignes={profil.competences} testid="profil-competences" />
          </div>
          <div>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              {t('monProfil.langues')}
            </p>
            <PastillesCompetences lignes={profil.langues} testid="profil-langues" />
          </div>
        </div>
      </div>

      {ouverte && (
        <ModifierMonProfil
          profil={profil}
          nom={nom}
          catalogue={catalogue}
          onClose={() => setOuverte(false)}
          onEnregistre={charger}
        />
      )}
    </>
  );
}

export default MonProfilEnrichi;
